import { useEffect, useMemo, useRef } from 'react';

import { useQueryAllWorkspaces, useQueryUsers } from '../../../authz/authz.hooks';
import { EnrichedWorkspace, EnrichedWorkspacesResult, User, Workspace } from '../types';

/**
 * Custom hook to fetch and enrich workspace data with user information
 *
 * Fetches all workspaces and users, then enriches each workspace with
 * the corresponding user's display name and login principal.
 *
 * @returns Object containing enriched workspaces, maps for efficient lookup, and loading/error states
 */
export function useEnrichedWorkspaces(): EnrichedWorkspacesResult {
  // Refs to track pagination state and prevent race conditions
  const isFetchingWorkspacesRef = useRef(false);
  const isFetchingUsersRef = useRef(false);

  // Fetch all workspaces with caching
  const workspacesQuery = useQueryAllWorkspaces(
    {},
    {
      // Cache workspace data for 60 minutes without refetching
      staleTime: 1000 * 60 * 60, // 60 minutes
      // Keep cached data in memory for 60 minutes
      cacheTime: 1000 * 60 * 60, // 60 minutes
      // Prevent automatic refetches - data rarely changes
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      select: (data) => {
        if (!data?.pages) return { pages: [], pageParams: [] };

        const workspacesFromAllPages = data.pages.flatMap((page) => page.workspaces || []);
        return { pages: workspacesFromAllPages, pageParams: data.pageParams };
      },
    }
  );

  const allWorkspaces = (workspacesQuery.data?.pages || []) as Workspace[];

  // Automatically fetch all workspace pages
  // Fixed: Use ref to prevent race conditions and removed query object from dependencies
  useEffect(() => {
    const shouldFetch =
      workspacesQuery.hasNextPage && !workspacesQuery.isFetchingNextPage && !isFetchingWorkspacesRef.current;

    if (shouldFetch) {
      isFetchingWorkspacesRef.current = true;
      workspacesQuery
        .fetchNextPage()
        .catch((error) => {
          console.error('[useEnrichedWorkspaces] Error fetching workspace page:', error);
        })
        .finally(() => {
          isFetchingWorkspacesRef.current = false;
        });
    }
  }, [workspacesQuery.hasNextPage, workspacesQuery.isFetchingNextPage]);

  // Fetch all users with caching
  const usersQuery = useQueryUsers(
    {},
    {
      // Cache user data for 60 minutes without refetching
      staleTime: 1000 * 60 * 60, // 60 minutes
      // Keep cached data in memory for 60 minutes
      cacheTime: 1000 * 60 * 60, // 60 minutes
      // Prevent automatic refetches - data rarely changes
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      select: (data) => {
        if (!data?.pages) return { pages: [], pageParams: [] };

        const usersFromAllPages = data.pages.flatMap((page) => page.users || []);
        return { pages: usersFromAllPages, pageParams: data.pageParams };
      },
    }
  );

  const allUsers = (usersQuery.data?.pages || []) as User[];

  // Automatically fetch all user pages
  // Fixed: Use ref to prevent race conditions and removed query object from dependencies
  useEffect(() => {
    const shouldFetch = usersQuery.hasNextPage && !usersQuery.isFetchingNextPage && !isFetchingUsersRef.current;

    if (shouldFetch) {
      isFetchingUsersRef.current = true;
      usersQuery
        .fetchNextPage()
        .catch((error) => {
          console.error('[useEnrichedWorkspaces] Error fetching user page:', error);
        })
        .finally(() => {
          isFetchingUsersRef.current = false;
        });
    }
  }, [usersQuery.hasNextPage, usersQuery.isFetchingNextPage]);

  // Create cached usersMap for efficient lookup: key = "users/<uuid>", value = user object
  const usersMap = useMemo(() => {
    return new Map<string, User>(allUsers.map((user) => [user.name, user]));
  }, [allUsers]);

  // Create enriched workspaces data structure that combines user and workspace data
  const enrichedWorkspaces = useMemo(() => {
    // Early return if no workspaces to process
    if (!allWorkspaces || allWorkspaces.length === 0) {
      return [];
    }

    // Enrich each workspace with user displayName and loginPrincipal
    return allWorkspaces.map((workspace): EnrichedWorkspace => {
      // Determine which field contains the user UUID
      const userRef =
        workspace.displayName === 'Personal Workspace' ? workspace.personalWorkspaceSubject : workspace.creator;

      // Look up the user in the map
      const user = userRef ? usersMap.get(userRef) : null;

      // Return enriched workspace object
      return {
        ...workspace,
        userDisplayName: user?.displayName || 'Unknown',
        userLoginPrincipal: user?.loginPrincipal || 'Unknown',
      };
    });
  }, [allWorkspaces, usersMap]);

  // Create cached workspacesMap for efficient lookup: key = "workspaces/<uuid>", value = workspace object
  const workspacesMap = useMemo(() => {
    return new Map<string, EnrichedWorkspace>(enrichedWorkspaces.map((workspace) => [workspace.name, workspace]));
  }, [enrichedWorkspaces]);

  const isLoading = workspacesQuery.isLoading || usersQuery.isLoading;
  const isError = workspacesQuery.isError || usersQuery.isError;

  return {
    enrichedWorkspaces,
    workspacesMap,
    usersMap,
    isLoading,
    isError,
  };
}
